import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Compass, KanbanSquare, BarChart3, Crosshair } from "lucide-react";
import { DiscoverTab } from "@/components/truffl/DiscoverTab";
import { PipelineTab } from "@/components/truffl/PipelineTab";
import { AnalyticsTab } from "@/components/truffl/AnalyticsTab";
import { TrufflTour } from "@/components/truffl/TrufflTour";
import { WeeklyGoalCard } from "@/components/truffl/WeeklyGoalCard";
import { TrufflPipeline } from "@/components/TrufflPipeline";

export default function TrufflPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [tab, setTab] = useState("discover");

  const refresh = useCallback(() => {
    api.get("/truffl/leads").then(({ data }) => setLeads(data || [])).catch(() => {});
    api.get("/truffl/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/truffl/config").then(({ data }) => setConfig(data)).catch(() => {});
    api.get("/truffl/preferences").then(({ data }) => setPrefs(data)).catch(() => setPrefs({}));
    refresh();
  }, [refresh]);

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 md:px-10" data-testid="truffl-page">
      <TrufflTour />
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Crosshair className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl">{t("truffl.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("truffl.subtitle")}
          </p>
        </div>
      </div>

      <TrufflPipeline refreshKey={leads.length} />
      <WeeklyGoalCard refreshKey={leads.length} />

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList>
          <TabsTrigger value="discover" data-testid="truffl-tab-discover">
            <Compass className="mr-1.5 h-4 w-4" /> {t("truffl.tab_discover")}
          </TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="truffl-tab-pipeline">
            <KanbanSquare className="mr-1.5 h-4 w-4" /> {t("truffl.tab_pipeline")}{leads.length ? ` (${leads.length})` : ""}
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="truffl-tab-analytics">
            <BarChart3 className="mr-1.5 h-4 w-4" /> {t("truffl.tab_analytics")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="discover" className="mt-6">
          <DiscoverTab key={prefs ? "p1" : "p0"} niches={config?.niches}
            initialSkill={params.get("skill") || prefs?.skill || ""}
            initialCity={params.get("city") || prefs?.city || ""}
            onLeadsAdded={() => { refresh(); setTab("pipeline"); }} />
        </TabsContent>
        <TabsContent value="pipeline" className="mt-6">
          <PipelineTab leads={leads} stages={config?.stages} onRefresh={refresh} />
        </TabsContent>
        <TabsContent value="analytics" className="mt-6">
          <AnalyticsTab analytics={analytics} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
